import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { scrollToTop } from '../utils/mobileUtils';
import { telehealthServices } from '../data/telehealthServices';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  const servicesMenu = telehealthServices.map((s) => ({
    name: s.navLabel,
    path: `/services/${s.slug}`,
  }));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    scrollToTop();
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && activeDropdown) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeDropdown]);

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    if (newMenuState) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
    setMobileServicesOpen(false);
    document.body.classList.remove('mobile-menu-open');
  };

  const toggleDropdown = (dropdown) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };

  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const isActive = (path) => location.pathname === path;
  const servicesSectionActive =
    location.pathname === '/services' || location.pathname.startsWith('/services/');

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo" onClick={closeMenu}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Logo size="medium" />
            </motion.div>
          </Link>

          <nav className="nav-desktop">
            <ul className="nav-menu">
              <li className="nav-item">
                <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
                  Home
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>
                  About
                </Link>
              </li>
              <li className="nav-item dropdown">
                <button
                  type="button"
                  className={`nav-link dropdown-toggle ${servicesSectionActive ? 'active' : ''} ${
                    activeDropdown === 'services' ? 'active' : ''
                  }`}
                  onClick={() => toggleDropdown('services')}
                >
                  Services
                  <ChevronDown className="chevron" />
                </button>
                {activeDropdown === 'services' && (
                  <ul className="dropdown-menu">
                    <li className="dropdown-category">
                      <span className="category-title">Telehealth services</span>
                      <Link to="/services" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                        View all services
                      </Link>
                      {servicesMenu.map((service, index) => (
                        <Link key={index} to={service.path} className="dropdown-link">
                          {service.name}
                        </Link>
                      ))}
                    </li>
                  </ul>
                )}
              </li>
            </ul>
          </nav>

          <div className="header-actions">
            <Link to="/services" className="header-action-link">
              Pricing & services
            </Link>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/contact" className="btn btn-primary">
                Contact us
              </Link>
            </motion.div>
          </div>

          <button type="button" className="mobile-menu-btn" onClick={toggleMenu} aria-label="Toggle menu">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container">
              <ul className="mobile-nav-menu">
                <li>
                  <Link to="/" className="mobile-nav-link" onClick={closeMenu}>
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="mobile-nav-link" onClick={closeMenu}>
                    About
                  </Link>
                </li>
                <li className="mobile-dropdown">
                  <button
                    type="button"
                    className={`mobile-dropdown-toggle ${mobileServicesOpen ? 'active' : ''}`}
                    onClick={toggleMobileServices}
                  >
                    Services
                    <ChevronDown className="chevron" />
                  </button>
                  {mobileServicesOpen && (
                    <ul className="mobile-dropdown-menu">
                      <li className="mobile-dropdown-category">
                        <span className="mobile-category-title">Telehealth</span>
                        <Link
                          to="/services"
                          className="mobile-dropdown-link"
                          onClick={() => {
                            closeMenu();
                            setMobileServicesOpen(false);
                          }}
                        >
                          View all services
                        </Link>
                        {servicesMenu.map((service, index) => (
                          <Link
                            key={index}
                            to={service.path}
                            className="mobile-dropdown-link"
                            onClick={() => {
                              closeMenu();
                              setMobileServicesOpen(false);
                            }}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <Link to="/contact" className="mobile-nav-link" onClick={closeMenu}>
                    Contact us
                  </Link>
                </li>
              </ul>
              <div className="mobile-actions">
                <Link to="/contact" className="btn btn-primary btn-full" onClick={closeMenu}>
                  Contact us
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
